import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../../utils/api';

function JobAppointments({ jobId }) {
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    appointment_date: '',
    start_time: '',
    end_time: '',
    appointment_type: 'Site Survey',
    engineer_id: '',
    status: 'scheduled',
    notes: ''
  });

  const appointmentTypes = [
    'Site Survey',
    'Installation',
    'Post-Install Inspection',
    'Customer Training',
    'Repair Visit',
    'Callback',
    'Aborted Visit'
  ];

  const statuses = [
    'scheduled',
    'confirmed',
    'on_route',
    'in_progress',
    'completed',
    'cancelled',
    'aborted'
  ];

  useEffect(() => {
    fetchAppointments();
    fetchEmployees();
  }, [jobId]);

  const fetchAppointments = async () => {
    try {
      const res = await apiFetch(`/api/job-appointments/job/${jobId}`);
      const data = await res.json();
      setAppointments(data);
    } catch (err) {
      console.error('Error fetching appointments:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await apiFetch('/api/employees');
      const data = await res.json();
      setEmployees(data);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      job_id: parseInt(jobId),
      ...formData,
      engineer_id: formData.engineer_id ? parseInt(formData.engineer_id) : null
    };

    try {
      const url = editingId 
        ? `/api/job-appointments/${editingId}`
        : '/api/job-appointments';
      
      const method = editingId ? 'PUT' : 'POST';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        fetchAppointments();
        resetForm();
      }
    } catch (err) {
      console.error('Error saving appointment:', err);
    }
  };

  const handleEdit = (appointment) => {
    setEditingId(appointment.id);
    setFormData({
      appointment_date: appointment.appointment_date?.split('T')[0] || '',
      start_time: appointment.start_time || '',
      end_time: appointment.end_time || '',
      appointment_type: appointment.appointment_type,
      engineer_id: appointment.engineer_id || '',
      status: appointment.status,
      notes: appointment.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this appointment?')) return;

    try {
      await apiFetch(`/api/job-appointments/${id}`, {
        method: 'DELETE'
      });
      fetchAppointments();
    } catch (err) {
      console.error('Error deleting appointment:', err);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      appointment_date: '',
      start_time: '',
      end_time: '',
      appointment_type: 'Site Survey',
      engineer_id: '',
      status: 'scheduled',
      notes: ''
    });
  };

  const formatStatus = (status) => {
    return status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-green-100 text-green-800',
      on_route: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-orange-100 text-orange-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
      aborted: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white rounded-lg border-2 border-gray-300 shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Appointments</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg transition font-medium"
        >
          {showForm ? 'Cancel' : '+ New Appointment'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="p-6 bg-gray-50 border-b-2 border-gray-300">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Date *</label>
              <input
                type="date"
                required
                value={formData.appointment_date}
                onChange={(e) => setFormData({...formData, appointment_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Start Time</label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">End Time</label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Type *</label>
              <select
                required
                value={formData.appointment_type}
                onChange={(e) => setFormData({...formData, appointment_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {appointmentTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Engineer</label>
              <select
                value={formData.engineer_id}
                onChange={(e) => setFormData({...formData, engineer_id: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Unassigned</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Status *</label>
              <select
                required
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {statuses.map(status => (
                  <option key={status} value={status}>{formatStatus(status)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
            <textarea
              rows="3"
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Visit notes, outcomes, customer feedback..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              className="px-6 py-2 bg-gray-300 text-black rounded-lg hover:bg-gray-400 transition font-medium"
            >
              {editingId ? 'Update Appointment' : 'Create Appointment'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition font-medium"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Appointments List */}
      <div className="p-6">
        {appointments.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No appointments scheduled yet.</p>
        ) : (
          <div className="space-y-4">
            {appointments.map((apt) => (
              <div key={apt.id} className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 transition">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-lg font-bold text-blue-700">{apt.appointment_type}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(apt.status)}`}>
                        {formatStatus(apt.status)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-600">
                      <div>📅 <strong>Date:</strong> {new Date(apt.appointment_date).toLocaleDateString('en-GB')}</div>
                      <div>⏰ <strong>Time:</strong> {apt.start_time || 'TBD'} {apt.end_time && `- ${apt.end_time}`}</div>
                      <div>👤 <strong>Engineer:</strong> {apt.first_name ? `${apt.first_name} ${apt.last_name}` : 'Unassigned'}</div>
                      <div>📝 <strong>Created:</strong> {new Date(apt.created_at).toLocaleDateString('en-GB')}</div>
                    </div>
                    {apt.notes && (
                      <div className="mt-2 p-2 bg-yellow-50 border-l-4 border-yellow-400 text-sm text-gray-700">
                        <strong>Notes:</strong> {apt.notes}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(apt)}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(apt.id)}
                      className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default JobAppointments;
